import { parseClass } from "@snapcss/parser";
import { resolve } from "./resolver";
import { applyStyles, removeStyles } from "./applier";

interface DarkEntry {
  el: Element;
  styles: Record<string, string>;
  styleKeys: string[];
  preDarkStyles?: Record<string, string>;
  darkApplied?: boolean;
}

const darkEntries: DarkEntry[] = [];
let forcedDark: boolean | null = null;

// Override OS preference — pass null to revert to matchMedia.
export function setDarkMode(isDark: boolean | null): void {
  forcedDark = isDark;
  applyDarkMode();
}

export function storeDarkClass(el: Element, snapClasses: string[]): void {
  for (const cls of snapClasses) {
    const parsed = parseClass(cls);
    if (parsed.modifier?.type !== "dark") continue;

    const styles = resolve({ ...parsed, modifier: null });
    if (!Object.keys(styles).length) continue;

    darkEntries.push({ el, styles, styleKeys: Object.keys(styles) });
  }
}

function snapshotStyles(el: Element, keys: string[]): Record<string, string> {
  const elStyle = (el as HTMLElement).style;
  const snapshot: Record<string, string> = {};
  for (const key of keys) {
    const cssKey = key.replace(/([A-Z])/g, (m) => `-${m.toLowerCase()}`);
    snapshot[key] = elStyle.getPropertyValue(cssKey);
  }
  return snapshot;
}

export function applyDarkMode(): void {
  const isDark =
    forcedDark ?? window.matchMedia("(prefers-color-scheme: dark)").matches;
  for (const entry of darkEntries) {
    if (isDark) {
      // Snapshot base styles before overwriting so we can restore them later
      entry.preDarkStyles = snapshotStyles(entry.el, entry.styleKeys);
      applyStyles(entry.el, entry.styles);
      entry.darkApplied = true;
    } else if (entry.darkApplied) {
      // Restore base styles — only remove properties that had no base value
      const pre = entry.preDarkStyles ?? {};
      const toRestore: Record<string, string> = {};
      const toRemove: string[] = [];
      for (const key of entry.styleKeys) {
        if (pre[key]) toRestore[key] = pre[key];
        else toRemove.push(key);
      }
      if (Object.keys(toRestore).length) applyStyles(entry.el, toRestore);
      if (toRemove.length) removeStyles(entry.el, toRemove);
      entry.darkApplied = false;
    }
    // if !isDark and never applied, do nothing — base styles are untouched
  }
}

export function setupDarkModeListener(): void {
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", applyDarkMode);
}

export function clearDarkEntries(): void {
  darkEntries.length = 0;
}
